import PropTypes from 'prop-types';
import React from 'react';

import AcceleratedPaymentMethods from 'components/AcceleratedPaymentMethods';
import Button, { TYPES } from 'components/Button';
import CartCount from 'components/CartCount';
import CartGiftMessage from 'components/CartGiftMessage';
import CartGiftMessagePrompt from 'components/CartGiftMessagePrompt';
import CartOrderSummary from 'components/CartOrderSummary';
import CloseIcon from 'components/CloseIcon';
import { DrawerPanel, DrawerHeader, DrawerBody, DrawerFooter } from 'components/Drawer';
import IconButton from 'components/IconButton';
import LoadingState from 'components/LoadingState';
import CartItem from 'containers/CartItemContainer';
import CartUpsell from 'containers/CartUpsellContainer';
import CartUpsellV2 from 'containers/CartUpsellV2Container';
import FreeShippingIndicator from 'containers/FreeShippingIndicatorContainer';
import ReturnsInsuranceUpsell from 'containers/ReturnsInsuranceUpsellContainer';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Settings from 'utils/Settings';
import { navigateToUrl } from 'utils/Url';

import CartAlert from './CartAlert';
import Empty from './CartEmptyState';
import TopContent from './CartTopContent';

const CartOverviewDrawerPanel = ({
  visible,
  close,
  itemCount,
  hasFreeShippingIndicator,
  isEmpty,
  navigate,
  errorMessage,
  message,
  newItemsBeingAdded,
  itemIds,
  isGiftMessagingOn,
  giftMessage,
  viewGiftMessage,
  subtotalWithCurrency,
  hasFreeShipping,
  showUpsell,
  useLogoFreePackaging,
  isSettingGiftingOptions,
  isLogoFreePackagingAvailable,
  checkoutUrl,
  isPriceChangeMessagingEnabled,
  acceleratedPaymentMethodsData,
  trackAcceleratedPaymentMethodClick,
}) => {
  const { variantIds: returnsInsuranceVariantIds } = Settings.returnsInsuranceConfig || {};

  return (
    <DrawerPanel position="left" visible={visible}>
      <DrawerHeader>
        <div className="CartOverviewDrawerPanel__padded-row">
          <div className="CartOverviewDrawerPanel__header">
            <div className="CartOverviewDrawerPanel__close-button">
              <IconButton onClick={close} id="cart-close-button">
                <CloseIcon>{I18n.localize('cart.closeCart')}</CloseIcon>
              </IconButton>
            </div>
            <CartCount itemCount={itemCount} />
            {hasFreeShippingIndicator ? <FreeShippingIndicator /> : <TopContent />}
          </div>
          {!hasFreeShippingIndicator && <hr className="CartOverviewDrawerPanel__horizontal-rule" />}
        </div>
      </DrawerHeader>

      <DrawerBody>
        {isPriceChangeMessagingEnabled && message && (
          <div className="CartOverviewDrawerPanel__padded-row">
            <CartAlert message={message} />
            <hr className="CartOverviewDrawerPanel__horizontal-rule" />
          </div>
        )}
        {errorMessage && (
          <div className="CartOverviewDrawerPanel__padded-row">
            <CartAlert message={errorMessage} />
            <hr className="CartOverviewDrawerPanel__horizontal-rule" />
          </div>
        )}

        {isEmpty ? (
          <Empty navigate={navigate} />
        ) : (
          <>
            <div className="CartOverviewDrawerPanel__line-items">
              {!isEmpty && (
                <>
                  {(() => {
                    // Get unique items while preserving order
                    const uniqueIds = [...new Set([...newItemsBeingAdded, ...itemIds])];
                    const nonInsuranceItems = uniqueIds.filter(
                      id => !returnsInsuranceVariantIds?.includes(id)
                    );

                    // Render first item if it exists
                    if (nonInsuranceItems[0]) {
                      return (
                        <div
                          className="CartOverviewDrawerPanel__line-item-container"
                          key={`CartOverviewDrawerPanel__line-item-container--${nonInsuranceItems[0]}`}
                        >
                          <CartItem
                            id={nonInsuranceItems[0]}
                            key={`CartOverviewDrawerPanel__line-item--${nonInsuranceItems[0]}`}
                          />
                          <div className="CartOverviewDrawerPanel__padded-row">
                            <hr className="CartOverviewDrawerPanel__horizontal-rule" />
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* We want the Returns Insurance Upsell to be the second item in the list */}
                  <ReturnsInsuranceUpsell />

                  {(() => {
                    const uniqueIds = [...new Set([...newItemsBeingAdded, ...itemIds])];
                    const nonInsuranceItems = uniqueIds.filter(
                      id => !returnsInsuranceVariantIds?.includes(id)
                    );

                    // Skip the first item since it's already rendered
                    return nonInsuranceItems.slice(1).map(id => (
                      <div
                        className="CartOverviewDrawerPanel__line-item-container"
                        key={`CartOverviewDrawerPanel__line-item-container--${id}`}
                      >
                        <CartItem id={id} key={`CartOverviewDrawerPanel__line-item--${id}`} />
                        <div className="CartOverviewDrawerPanel__padded-row">
                          <hr className="CartOverviewDrawerPanel__horizontal-rule" />
                        </div>
                      </div>
                    ));
                  })()}
                </>
              )}
            </div>
          </>
        )}
      </DrawerBody>

      {!isEmpty && (
        <DrawerFooter>
          <div className="CartOverviewDrawerPanel__order-summary">
            <div className="CartOverviewDrawerPanel__padded-row">
              <hr className="CartOverviewDrawerPanel__horizontal-rule" />
              {(isGiftMessagingOn || isLogoFreePackagingAvailable) && (
                <div className="CartOverviewDrawerPanel__gift-messaging">
                  <hr className="CartOverviewDrawerPanel__horizontal-rule" />

                  {isSettingGiftingOptions && <LoadingState />}
                  {giftMessage || useLogoFreePackaging ? (
                    <CartGiftMessage
                      viewGiftMessage={viewGiftMessage}
                      giftMessage={giftMessage}
                      useLogoFreePackaging={useLogoFreePackaging}
                    />
                  ) : (
                    <CartGiftMessagePrompt
                      viewGiftMessage={viewGiftMessage}
                      isLogoFreePackagingAvailable={isLogoFreePackagingAvailable}
                    />
                  )}

                  <hr className="CartOverviewDrawerPanel__horizontal-rule" />
                </div>
              )}

              <CartOrderSummary subtotal={subtotalWithCurrency} hasFreeShipping={hasFreeShipping} />

              <div className="CartOverviewDrawePanel__button-wrapper">
                <Button type={TYPES.DEFAULT} linkTo={checkoutUrl} isFullWidth isUppercase>
                  {I18n.localize('cart.checkout')}
                </Button>
              </div>

              {acceleratedPaymentMethodsData?.supportedDigitalWallets?.length > 0 && (
                <AcceleratedPaymentMethods
                  checkoutUrl={checkoutUrl}
                  onLinkClick={trackAcceleratedPaymentMethodClick}
                  supportedDigitalWallets={acceleratedPaymentMethodsData.supportedDigitalWallets}
                />
              )}

              {Settings.cartUpsellsV2Enabled && (
                <div className="CartOverviewDrawerPanel__upsell">
                  <CartUpsellV2 />
                </div>
              )}

              {showUpsell && !Settings.cartUpsellsV2Enabled && (
                <div className="CartOverviewDrawerPanel__upsell">
                  <CartUpsell />
                </div>
              )}
            </div>
          </div>
        </DrawerFooter>
      )}

      <style jsx>{`
        .CartOverviewDrawerPanel__padded-row {
          position: relative;
          padding: 0 ${Theme.get('spacing') * 4}px;
        }

        .CartOverviewDrawerPanel__horizontal-rule {
          border: none;
          height: 1px;
          margin: 0;
          background-color: ${Theme.get('colors-black-20')};
        }

        .CartOverviewDrawerPanel__header {
          position: relative;
          padding: ${Theme.get('spacing') * 4}px 0 0;
        }

        .CartOverviewDrawerPanel__close-button {
          position: absolute;
          top: ${Theme.get('spacing') * 4}px;
          left: 0;
          z-index: 1;
        }

        .CartOverviewDrawerPanel__order-summary {
          padding-bottom: ${Theme.get('spacing') * 4}px;
          margin-top: -1px;
        }

        .CartOverviewDrawerPanel__upsell {
          margin-top: ${Theme.get('spacing') * 2.5}px;
        }

        .CartOverviewDrawePanel__button-wrapper {
          margin-bottom: ${acceleratedPaymentMethodsData?.supportedDigitalWallets?.length > 0
            ? Theme.get('spacing-px')
            : 0};
        }

        .CartOverviewDrawePanel__button-wrapper--sticky {
          position: sticky;
          bottom: 0;
          padding: ${Theme.get('spacing') * 1.5}px 0;
        }

        .CartOverviewDrawePanel__button-wrapper--sticky::before {
          content: '';
          position: absolute;
          pointer-events: none;
          left: 0;
          bottom: 0;
          height: 115%;
          width: 100%;
          background: linear-gradient(
            180deg,
            hsla(0, 0%, 100%, 0),
            hsla(0, 0%, 100%, 0.8) 20%,
            hsla(0, 0%, 100%, 0.8)
          );
        }
      `}</style>

      <style jsx global>{`
        .CartOverviewDrawerPanel__header .CartCount__icon {
          margin: 0 auto;
          width: ${Theme.get('spacing') * 4}px;
          display: block;
        }
      `}</style>
    </DrawerPanel>
  );
};

CartOverviewDrawerPanel.propTypes = {
  hasFreeShipping: PropTypes.bool,
  hasFreeShippingIndicator: PropTypes.bool,
  subtotalWithCurrency: PropTypes.string,
  itemIds: PropTypes.arrayOf(PropTypes.string),
  itemCount: PropTypes.number,
  close: PropTypes.func.isRequired,
  showUpsell: PropTypes.bool,
  navigate: PropTypes.func,
  isEmpty: PropTypes.bool,
  newItemsBeingAdded: PropTypes.arrayOf(PropTypes.string),
  errorMessage: PropTypes.string,
  message: PropTypes.string,
  giftMessage: PropTypes.string,
  viewGiftMessage: PropTypes.func,
  isGiftMessagingOn: PropTypes.bool,
  visible: PropTypes.bool,
  useLogoFreePackaging: PropTypes.bool,
  isSettingGiftingOptions: PropTypes.bool,
  isLogoFreePackagingAvailable: PropTypes.bool,
  checkoutUrl: PropTypes.string,
  isPriceChangeMessagingEnabled: PropTypes.bool,
  showShoprunner: PropTypes.bool,
  acceleratedPaymentMethodsData: PropTypes.shape({
    supportedDigitalWallets: PropTypes.arrayOf(PropTypes.string),
  }),
  trackAcceleratedPaymentMethodClick: PropTypes.func,
};

CartOverviewDrawerPanel.defaultProps = {
  hasFreeShipping: false,
  hasFreeShippingIndicator: false,
  itemCount: 0,
  itemIds: [],
  showUpsell: false,
  navigate: navigateToUrl,
  isEmpty: false,
  newItemsBeingAdded: [],
  errorMessage: null,
  message: null,
  giftMessage: '',
  viewGiftMessage: () => {},
  isGiftMessagingOn: false,
  visible: true,
  useLogoFreePackaging: false,
  isSettingGiftingOptions: false,
  isLogoFreePackagingAvailable: false,
  isPriceChangeMessagingEnabled: false,
  showShoprunner: false,
  acceleratedPaymentMethodsData: {},
  trackAcceleratedPaymentMethodClick: () => {},
  checkoutUrl: '/checkout',
  subtotalWithCurrency: null,
};

export default CartOverviewDrawerPanel;
